import React, { useContext, useState } from "react";
import { Layout, Menu, Dropdown, Drawer, Button, Grid } from "antd";
import { HomeOutlined, HistoryOutlined, LogoutOutlined, CalendarOutlined, FileTextOutlined, FileProtectOutlined, DollarOutlined, ClockCircleOutlined, MenuOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import ProfileAvatar from "./ProfileAvatar";

const { Header, Content, Footer } = Layout;
const { useBreakpoint } = Grid;

const menuItems = [
  { key: "/", icon: <HomeOutlined />, label: "Home" },
  { key: "/attendance", icon: <HistoryOutlined />, label: "My Attendance" },
  { key: "/leave", icon: <FileTextOutlined />, label: "Leave" },
  { key: "/wfh", icon: <HomeOutlined />, label: "WFH" },
  { key: "/holidays", icon: <CalendarOutlined />, label: "Holidays" },
  { key: "/policies", icon: <FileProtectOutlined />, label: "Policies" },
  { key: "/reimbursements", icon: <DollarOutlined />, label: "Reimbursements" },
  { key: "/overtime", icon: <ClockCircleOutlined />, label: "Overtime" },
];

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="9" fill="rgba(255,255,255,0.18)" />
    <path d="M10 16.5l4 4 8-8.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Main = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, logout } = useContext(AuthContext);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userMenu = (
    <Menu items={[{ key: "logout", icon: <LogoutOutlined />, label: "Logout", onClick: logout }]} />
  );

  const handleMenuClick = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  return (
    <Layout style={{ minHeight: "100%", background: "var(--i-ems-bg)" }}>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)",
          paddingLeft: 16,
          paddingRight: 16,
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 2px 8px rgba(55,48,163,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: "#fff" }} />}
              onClick={() => setDrawerOpen(true)}
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <LogoMark />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 17, whiteSpace: "nowrap", letterSpacing: 0.2 }}>
              I-EMS
            </span>
          </div>
        </div>

        {!isMobile && (
          <Menu
            theme="dark"
            mode="horizontal"
            style={{ background: "transparent", flex: 1, marginLeft: 32, borderBottom: "none" }}
            selectedKeys={[(menuItems.find((m) => m.key !== "/" && (location.pathname === m.key || location.pathname.startsWith(m.key + "/"))) || { key: location.pathname }).key]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        )}

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 20 }}>
          <NotificationBell />
          <Dropdown overlay={userMenu} placement="bottomRight" trigger={["click"]}>
            <div style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "#fff" }}>
              <ProfileAvatar />
              {!isMobile && <span>{userProfile?.name || "Employee"}</span>}
            </div>
          </Dropdown>
        </div>
      </Header>

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30 }}>
              <svg width="30" height="30" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="9" fill="var(--i-ems-primary-light)" />
                <path d="M10 16.5l4 4 8-8.5" stroke="var(--i-ems-primary)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span style={{ fontWeight: 700 }}>I-EMS</span>
          </div>
        }
        placement="left"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        bodyStyle={{ padding: 8 }}
        width={272}
        contentWrapperStyle={{ borderRadius: "0 var(--i-ems-radius-lg) var(--i-ems-radius-lg) 0", overflow: "hidden" }}
      >
        <Menu
          mode="inline"
          selectedKeys={[(menuItems.find((m) => m.key !== "/" && (location.pathname === m.key || location.pathname.startsWith(m.key + "/"))) || { key: location.pathname }).key]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: "none" }}
        />
      </Drawer>

      <Content style={{ padding: isMobile ? "12px" : "24px", width: "100%", maxWidth: 1120, margin: "0 auto" }}>
        {children}
      </Content>
      <Footer style={{ textAlign: "center", color: "var(--i-ems-text-secondary)", fontSize: 12, background: "transparent" }}>
        I-EMS Portal
      </Footer>
    </Layout>
  );
};

export default Main;
